import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FullCalendarComponent, CalendarOptions } from '@fullcalendar/angular';
import { toMoment } from '@fullcalendar/moment';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { ConstantMan } from '../../../core/services/constantMan';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import * as moment from 'moment';
import { Moment } from 'moment';
import { CalendarEvent } from '../../../core/models/interface/calendar-event';
import { NumberFormatterService } from '../../../core/services/number.formatter.service';
import { isPlatformBrowser } from '@angular/common';
import { timer } from 'rxjs/internal/observable/timer';
import { ContainsSubscriptions } from '../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import { setEventType } from '../../portal/profile/captain/boatyard/form/charter-schedule/charter-schedule.component';

@Component({
  selector: 'app-calendar',
  templateUrl: './app-calendar.component.html',
  styleUrls: ['./app-calendar.component.scss'],
})
export class AppCalendarComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @ViewChild(FullCalendarComponent)
  ucCalendar: FullCalendarComponent;

  @Output()
  hasAllocations: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  hasUnavailable: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  selectedDate: EventEmitter<Moment> = new EventEmitter<Moment>();

  @Output()
  selectedEvent: EventEmitter<any> = new EventEmitter<any>();

  private _selectedBoatId = new BehaviorSubject<number>(-1);
  @Input()
  set selectedBoatId(value) {
    this._selectedBoatId.next(value);
  }
  get selectedBoatId() {
    return this._selectedBoatId.getValue();
  }

  @Input()
  calendarOptions: CalendarOptions;

  @Input()
  private currentCharter: Charter;

  @Input()
  private isEdit: boolean;

  @Input()
  boatChangeEvent: Subject<number>;

  @Input()
  currentCharterDate: string;

  @Input()
  validateDate: (any) => boolean;

  events = [];
  vessel: Vessel;
  chartersForVessel: Charter[];
  vesselUnavailabilities: any[];
  allowedToSelectDates = true;
  isBrowser: boolean;
  loadingCalendarEvents = false;
  subs: Subscription[] = [];

  constructor(
    private vesselDao: VesselDAO,
    private charterDao: CharterDAO,
    private numFormatterService: NumberFormatterService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    if (this.isEdit) {
      this.allowedToSelectDates = false;
    }
    this.loadEvents(this.selectedBoatId);
    if (this.boatChangeEvent) {
      const boatChangeEventSub = this.boatChangeEvent.subscribe(
        (newVesselId) => {
          if (newVesselId) {
            this.loadEvents(newVesselId);
          }
        },
      );
      this.subs.push(boatChangeEventSub);
    }
  }

  setCalendarOptions(events): void {
    this.calendarOptions = {
      selectable: true,
      displayEventTime: false,
      eventClick: this.eventClick.bind(this),
      select: this.dayClick.bind(this),
      timeZone: 'UTC',
      events: events,
      themeSystem: 'standard',
      buttonIcons: {
        prev: 'chevron-left',
        next: 'chevron-right',
      },
      headerToolbar: {
        start: 'prev,next',
        center: 'title',
        end: 'today',
      },
      buttonText: {
        prev: 'Prev',
        next: 'Next',
      },
    };
  }

  loadEvents(vesselId: number): void {
    this.loadingCalendarEvents = true;
    let obs: Observable<any[]>;
    if (this.currentCharter && this.currentCharter.id) {
      obs = this.charterDao.loadChartersAfterId(
        this.currentCharter.id,
        vesselId,
      );
    } else {
      obs = this.charterDao.loadChartersAfterEpochForVessel(
        moment().subtract(1, 'month').unix(),
        vesselId,
      );
    }
    const combined = combineLatest([obs, timer(1000)]).subscribe(
      (completed) => {
        this.chartersForVessel = completed[0];
        const vesselSub = this.vesselDao
          .getById(vesselId)
          .subscribe((vessel) => {
            this.vessel = vessel;
            this.events = this.prepareVesselUnavailabilities();
            this.loadingCalendarEvents = false;
          });
        this.subs.push(vesselSub);
      },
    );
    this.subs.push(combined);
  }

  dayClick(dayClicked: any) {
    if (this.validateDate && !this.validateDate(dayClicked)) {
      return;
    }
    const type = this.getExistingEventTypes(dayClicked);
    if (type === ConstantMan.APP_CALENDAR.UNAVAILABLE) {
      this.hasUnavailable.emit(true);
    } else if (type === ConstantMan.APP_CALENDAR.ALLOCATED) {
      this.hasAllocations.emit(true);
      this.renderNewEvent(dayClicked);
    } else {
      this.renderNewEvent(dayClicked);
    }
  }

  public renderNewEvent(dayClicked: any): void {
    let calendarApi = this.ucCalendar.getApi();
    if (this.allowedToSelectDates) {
      const event = {
        eventType: ConstantMan.APP_CALENDAR.CHARTER_PLACEHOLDER,
        title: ConstantMan.APP_CALENDAR.CHARTER_PLACEHOLDER,
        start: dayClicked.start,
        end: moment(dayClicked.start).endOf('day').toISOString(),
        dialogInfo:
          'Represents the date of the charter you are currently adding.',
      };
      this.events.push(event);
      this.currentCharterDate = toMoment(dayClicked.start, calendarApi).format(
        'YYYY-MM-DD',
      );
      calendarApi.addEvent(event);
      this.allowedToSelectDates = false;
      this.selectedDate.emit(moment(this.currentCharterDate));
    }
  }

  public deleteByTitle(title: string): void {
    if (isPlatformBrowser(this.platformId)) {
      this.ucCalendar
        .getApi()
        .getEvents()
        .map((event) => {
          if (event.title === title) {
            event.remove();
          }
        });
    }
    this.events = this.events.filter(
      (event) => event.title !== ConstantMan.APP_CALENDAR.CHARTER_PLACEHOLDER,
    );
    this.allowedToSelectDates = true;
  }

  eventClick(info: any) {
    this.selectedEvent.emit(info.event._def.extendedProps);
  }

  private getExistingEventsForDay(dayClicked: any): Array<any> {
    let clientEvents = [];
    let calendarApi = this.ucCalendar.getApi();
    calendarApi.getEvents().map((event) => {
      const eventStart = moment(event.start).format('YYYY-MM-DD HH:MM');
      const dayClickedDate = toMoment(dayClicked.startDate, calendarApi).format(
        'YYYY-MM-DD HH:MM',
      );
      const eventEnd = moment(event.end).format('YYYY-MM-DD HH:MM');
      if (eventStart <= dayClickedDate && eventEnd >= dayClickedDate) {
        clientEvents.push(event);
      }
    });
    return clientEvents;
  }

  private getExistingEventTypes(dayClicked: any): string {
    const events = this.getExistingEventsForDay(dayClicked);
    let eventType = ConstantMan.APP_CALENDAR.AVAILABLE;
    for (let i = 0; i < events.length; i++) {
      if (events[i].eventType === ConstantMan.APP_CALENDAR.UNAVAILABLE) {
        eventType = ConstantMan.APP_CALENDAR.UNAVAILABLE;
        break;
      }
      if (
        events[i].eventType === ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION
      ) {
        eventType = ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION;
        break;
      }
      if (events[i].eventType === ConstantMan.APP_CALENDAR.ALLOCATED) {
        eventType = ConstantMan.APP_CALENDAR.ALLOCATED;
        break;
      }
    }
    return eventType;
  }

  private prepareVesselUnavailabilities(): any[] {
    const vesselEvents = [];
    if (this.vessel.calendarPreferences) {
      this.vessel.calendarPreferences.forEach((value) => {
        let eventType = setEventType(value);
        const calEvent: CalendarEvent = {
          eventType: eventType.eventType,
          title: eventType.title,
          start: value.fromDate,
          end: new Date(moment(value.toDate).subtract(1, 'minute').format()),
          color: eventType.color,
          dialogInfo: eventType.dialogInfo,
        };
        vesselEvents.push(calEvent);
      });
    }
    if (this.chartersForVessel) {
      // Filtering out charters that have been cancelled
      const filteredCharters = this.chartersForVessel.filter(
        (charter) => !charter.cancelled,
      );
      filteredCharters.forEach((charter) => {
        if (
          (this.currentCharter && this.currentCharter.id !== charter.id) ||
          !this.currentCharter
        ) {
          const eventColor = charter.paymentReceived ? 'green' : 'orange';
          const calEvent = {
            eventType: ConstantMan.APP_CALENDAR.ALLOCATED,
            title: `ID: ${charter.id} - ${charter.customerFirstName} ${charter.customerLastName}`,
            start: this.getUtcDate(charter.date).startOf('day').format(),
            end: this.getUtcDate(charter.date)
              .add(charter.duration, 'hours')
              .endOf('day')
              .format(),
            color: eventColor,
            charterId: charter.id,
            dialogInfo: `<h4>
              <b>Charter Date:</b> ${this.getUtcDate(charter.date).format(
                'MM-DD-YY',
              )} <br>
              <b>Deposit Status:</b> ${charter.depositStatus} <br>
              <b>Charter Duration:</b> ${charter.duration} hours <br>
              <b>Charter Start:</b> ${charter.startTime} <br>
              <b>Charter End:</b> ${charter.charterEndTime} <br>
              <b>Number of Passengers:</b> ${charter.numOfPassengers} <br>
              <b>Captain:</b> ${
                charter.captainFirstName
                  ? charter.captainFirstName
                  : 'Captain is '
              }
              ${charter.captainLastName ? charter.captainLastName : 'TBD'} <br>
              <a href="${
                window.location.origin + '/manage/manage-charter/' + charter.id
              }" target="_blank" style="color: #337AB7">Manage Charter</a> <br>
              </h4>`,
          };
          vesselEvents.push(calEvent);
        }
      });
    }
    if (this.currentCharterDate) {
      const calendarStartEnd = moment.utc(
        this.currentCharterDate,
        'MM-DD-YYYY',
      );
      const currentEvent = {
        eventType: ConstantMan.APP_CALENDAR.CHARTER_PLACEHOLDER,
        title: ConstantMan.APP_CALENDAR.CHARTER_PLACEHOLDER,
        start: calendarStartEnd,
        end: calendarStartEnd,
        dialogInfo:
          'Represents the date of the charter you are currently adding.',
      };
      vesselEvents.push(currentEvent);
    }
    this.setCalendarOptions(vesselEvents);
    return vesselEvents;
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  getUtcDate(date: any): Moment {
    return moment.utc(date, 'YYYY-MM-DD');
  }
}
